import React, {
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { getCurrentUser } from 'redux/selectors/users';

import { AngularContext } from 'react-app';

import ConversationModalContent from 'lecture_pages/components/ai-role-play/ai-conversation-modal';
import AiConversationContext, {
  AIRolePlayStatus,
  AudioRecordingStatus,
  Message,
  SessionData,
} from 'lecture_pages/components/ai-role-play/ai-conversation-modal/context';
import useAiConversationHandlers from 'lecture_pages/components/ai-role-play/hooks/use-ai-conversation-handlers';
import useAiRolePlaySubmissionPusherEvents from 'lecture_pages/components/ai-role-play/hooks/use-ai-role-play-submission-pusher-events';
import { RootState } from 'redux/schemas';
import useAiRolePlay from 'lecture_pages/components/ai-role-play/hooks/use-ai-role-play';
import { getCourse } from 'redux/selectors/course';
import RolePlaySubmissionModalStyles from './role-play-submission-modal-styles';

const NewConversationView = () => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [$state, PageLevelManager] = injectServices(['$state', 'PageLevelManager']);
  const rolePlayId = Number($state?.params?.rolePlayId);

  // The lecture page component runs useAiRolePlaySubmissionPusherEvents while mounted beneath
  // this modal. Disable it here when the modal was opened from a lecture page to avoid
  // duplicate Redux dispatches and a double points modal.
  const lastParentState = PageLevelManager?.lastParent()?.state;
  const isLecturePageInBackground = !!(lastParentState?.$$state?.()?.includes?.['lecture-page-parent']);

  const aiRolePlay = useAiRolePlay(rolePlayId);
  const courseCatalogId = aiRolePlay?.courseCatalogId;
  const course = useSelector((state: RootState) => getCourse(state, courseCatalogId));
  const { anonymizedIdentifier } = useSelector((state: RootState) => getCurrentUser(state));

  const [aiRolePlayStatus, setAiRolePlayStatus] = useState(AIRolePlayStatus.NOT_STARTED);
  const [showScenarioDetails, setShowScenarioDetails] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTimeout, setIsTimeout] = useState(false);
  const [isRolePlayStarting, setIsRolePlayStarting] = useState(false);
  const [openingNarrative, setOpeningNarrative] = useState('');
  const [isAwaitingAiResponse, setIsAwaitingAiResponse] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [sessionData, setSessionData] = useState<SessionData>({
    aiApiEndpoint: null,
    jwtToken: null,
    submissionId: null,
    expiresAt: null,
    languageLocale: null,
  });
  const [audioRecordingStatus, setAudioRecordingStatus] = useState(AudioRecordingStatus.NOT_STARTED);
  const isHardDeadlineExpired = !!(aiRolePlay?.hardDeadline && aiRolePlay?.expired);

  const {
    handleChunk,
    handleError,
    handleStartConversation,
  } = useAiConversationHandlers({
    aiRolePlayId: aiRolePlay?.id,
    setAiRolePlayStatus,
    setShowScenarioDetails,
    messages,
    setMessages,
    sessionData,
    setSessionData,
    setIsTimeout,
    abortControllerRef,
    setIsRolePlayStarting,
    openingNarrative,
    setOpeningNarrative,
    setIsAwaitingAiResponse,
  });

  useAiRolePlaySubmissionPusherEvents({
    anonymizedIdentifier,
    courseId: course?.id,
    aiRolePlayId: aiRolePlay?.id,
    courseCatalogId,
    pointsReceived: aiRolePlay?.pointsReceived,
    skillTags: [],
    enabled: !isLecturePageInBackground,
  });

  const resetConversation = useCallback(() => {
    // Abort any existing stream before resetting state
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS) {
      setMessages([]);
    }
    setAiRolePlayStatus(AIRolePlayStatus.NOT_STARTED);
    setShowScenarioDetails(true);
    setSessionData({
      aiApiEndpoint: null,
      jwtToken: null,
      submissionId: null,
      expiresAt: null,
      languageLocale: null,
    });
    setIsTimeout(false);
    setIsAwaitingAiResponse(false);
    setAudioRecordingStatus(AudioRecordingStatus.NOT_STARTED);
  }, [aiRolePlayStatus]);

  const exitToLesson = useCallback(() => {
    const lastParent = PageLevelManager?.lastParent?.();
    if (lastParent?.state?.name) {
      $state.go(lastParent.state.name, lastParent.params);
    } else {
      $state.go('dashboard');
    }
  }, [PageLevelManager, $state]);

  /**
   * Exits the chat. From the feedback screen (COMPLETED) this leaves the modal for
   * the lesson. Mid-conversation it confirms first, then resets — unless
   * skipConfirmation is true (inactivity timeout), which resets without confirming.
   */
  const handleExitChat = useCallback((skipConfirmation = false) => {
    if (aiRolePlayStatus === AIRolePlayStatus.COMPLETED) {
      exitToLesson();
    } else if (!skipConfirmation && aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS) {
      dispatch(openConfirmationDialog({
        title: t.FORM.ARE_YOU_SURE(),
        bodyText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.MESSAGE(),
        confirmText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.CONFIRM(),
        cancelText: t.FORM.CANCEL(),
        onConfirm: resetConversation,
      }));
    } else {
      resetConversation();
    }
  }, [aiRolePlayStatus, dispatch, resetConversation, exitToLesson]);

  const wrapperStyles = css`
    height: 100%;
    > div {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  `;

  return (
    <>
      <RolePlaySubmissionModalStyles />
      <AiConversationContext.Provider
        value={{
          handleExitChat,
          aiRolePlayStatus,
          setAiRolePlayStatus,
          showScenarioDetails,
          setShowScenarioDetails,
          aiRolePlayId: aiRolePlay.id,
          messages,
          setMessages,
          sessionData,
          setSessionData,
          isTimeout,
          setIsTimeout,
          abortControllerRef,
          isHardDeadlineExpired,
          isRolePlayStarting,
          setIsRolePlayStarting,
          openingNarrative,
          handleChunk,
          handleError,
          handleStartConversation,
          isAwaitingAiResponse,
          audioRecordingStatus,
          setAudioRecordingStatus,
        }}
      >
        <div css={wrapperStyles}>
          <ConversationModalContent />
        </div>
      </AiConversationContext.Provider>
    </>
  );
};

export default NewConversationView;
